import { useEffect, useState } from "react";
import { fetchTile } from "../lib/api";
import { isTileError, type TileResponse } from "../lib/types";

interface TileProps {
  /** Display name on the tile. */
  label: string;
  /** Backend endpoint that returns a TileMetric. */
  endpoint: string;
  /** Where to send the user when they tap the button. */
  link: string;
  /** If false, the amber/red aging chip is hidden (e.g. for in-transit metrics where age is normal). */
  showAging?: boolean;
  /** Refresh interval in milliseconds. Default 120000 (2 minutes). */
  refreshMs?: number;
}

/**
 * Single KPI tile. Auto-refreshes on its own interval so a stalled fetch on one
 * tile cannot block the others. Shows a stale badge if the most recent fetch failed.
 */
export function Tile({
  label,
  endpoint,
  link,
  showAging = true,
  refreshMs = 120_000,
}: TileProps) {
  const [data, setData] = useState<TileResponse | null>(null);
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const next = await fetchTile(endpoint);
        if (!cancelled) {
          setData(next);
          setStale(false);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setStale(true);
          setLoading(false);
          // Keep prior data on screen rather than blanking out
          console.error("tile fetch failed", endpoint, e);
        }
      }
    };

    load();
    const id = window.setInterval(load, refreshMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [endpoint, refreshMs]);

  const isError = data !== null && isTileError(data);
  const metric = data !== null && !isError ? data : null;

  // Aging colour: red wins over amber
  const flagState =
    !metric || !showAging
      ? "none"
      : metric.count_48h > 0
        ? "red"
        : metric.count_24h > 0
          ? "amber"
          : "none";

  const flagColour =
    flagState === "red"
      ? "bg-danger/15 text-danger ring-danger/40"
      : flagState === "amber"
        ? "bg-amber/15 text-amber ring-amber/40"
        : "bg-aqua/10 text-aqua ring-aqua/30";

  const flagText =
    flagState === "red"
      ? `${metric!.count_48h} over 48h`
      : flagState === "amber"
        ? `${metric!.count_24h} over 24h`
        : "On track";

  return (
    <div className="relative flex flex-col rounded-3xl border border-aqua/10 bg-gradient-to-br from-[#06192c] to-[#000019] p-8 shadow-glow">
      {stale && (
        <span className="absolute right-6 top-6 rounded-full bg-amber/20 px-3 py-1 text-xs font-medium text-amber">
          Stale
        </span>
      )}

      <div className="flex flex-1 flex-col gap-6">
        <h2 className="text-xl font-medium uppercase tracking-wide text-aqua">
          {label}
        </h2>

        <div className="flex items-baseline gap-4">
          <span className="tabular text-9xl font-semibold text-white">
            {loading ? (
              <span className="text-aqua/40">--</span>
            ) : metric ? (
              metric.count.toLocaleString()
            ) : (
              <span className="text-danger/60">!</span>
            )}
          </span>
        </div>

        {showAging && metric && (
          <div
            className={`inline-flex w-max items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium ring-1 ring-inset ${flagColour}`}
          >
            <span className="h-2 w-2 rounded-full bg-current" />
            {flagText}
          </div>
        )}
      </div>

      <a
        href={link}
        target="_blank"
        rel="noreferrer"
        className="mt-8 inline-flex items-center justify-center gap-2 rounded-2xl bg-mint px-6 py-4 text-lg font-semibold text-midnight transition hover:bg-aqua active:scale-[0.98]"
      >
        Open in NextX
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
          className="h-5 w-5"
        >
          <path
            fillRule="evenodd"
            d="M4 10a.75.75 0 0 1 .75-.75h8.69L11.22 7.03a.75.75 0 1 1 1.06-1.06l3.5 3.5a.75.75 0 0 1 0 1.06l-3.5 3.5a.75.75 0 1 1-1.06-1.06l2.22-2.22H4.75A.75.75 0 0 1 4 10z"
            clipRule="evenodd"
          />
        </svg>
      </a>
    </div>
  );
}
